// Voo remarcado: o voucher novo toma o lugar do antigo (marcado com a alteração
// solicitada) na lista de serviços. O par vem pronto do trip-api (`TripView.rebookings`);
// aqui só se lê. O antigo continua em `trip.vouchers` só para a linha "Antes" do novo.
import { isChangeRequestedVoucher, type TripView } from '@voll-intelligence/domain';
import { localHHMM, parseLocalIso } from './dates.js';

type Voucher = TripView['vouchers'][number];

/** O voo que o novo substituiu, como o card o escreve na linha "Antes": o primeiro
 *  trecho do voucher antigo — o mesmo trecho pelo qual o par casou. `departAt` é o
 *  relógio de PAREDE do aeroporto (a mesma leitura do `ServiceLeg.departAt`). */
export interface RebookedFrom {
  /** "LATAM 3340": companhia e número, sem logo ao lado para fazer as vezes do nome. */
  flightLabel: string;
  departAt: Date | null;
  departTime: string;
}

function rebookedFrom(old: Voucher): RebookedFrom | null {
  const segment = old.details?.kind === 'FLIGHT' ? old.details.segments[0] : undefined;
  if (!segment) return null;
  const flightLabel = [segment.airline?.trim(), segment.flightNumbers.join('/')]
    .filter(Boolean)
    .join(' ');
  return {
    flightLabel,
    departAt: parseLocalIso(segment.departLocal) ?? segment.departAt,
    departTime: localHHMM(segment.departLocal, segment.departAt),
  };
}

/** O que a lista de serviços precisa saber dos pares: quem sai (`replaced`) e de onde
 *  veio quem fica (`fromOf`, pelo id do novo). Um par cujo antigo não veio na viagem não
 *  esconde nada nem marca o novo: sem o antigo não há "Antes" a contar. */
export function rebookingIndex(trip: TripView): {
  replaced: ReadonlySet<string>;
  fromOf: ReadonlyMap<string, RebookedFrom>;
} {
  const replaced = new Set<string>();
  const fromOf = new Map<string, RebookedFrom>();
  for (const pair of trip.rebookings ?? []) {
    const old = trip.vouchers.find((v) => v.id === pair.replacesVoucherId);
    const from = old ? rebookedFrom(old) : null;
    if (!from) continue;
    replaced.add(pair.replacesVoucherId);
    fromOf.set(pair.voucherId, from);
  }
  return { replaced, fromOf };
}

/** O passo é de um voo com alteração solicitada: segue na lista (o viajante precisa vê-lo),
 *  mas não é "o próximo voo": não define o destaque, o cartão vivo, a sugestão de corrida
 *  nem a janela da capa. Um predicado só para todos os leitores de "o próximo". */
export function isChangeRequestedStop(stop: {
  service: { voucher: TripView['vouchers'][number] };
}): boolean {
  return isChangeRequestedVoucher(stop.service.voucher);
}
